import React from 'react';
import PropTypes from 'prop-types';
import Comment from '../input/Comment.component';
import {PopUp} from './PopUp.component';
import {TASK_ACTIONS} from 'utils/constants';
import translate from '../translations/translations.wrapper.jsx';
import {connect} from 'react-redux';
import {
    getTaskDraftCommentText,
    getActionParameters,
    getUsersSettings,
    getUsersCurrentContextId
} from "../store/application.reducers";
import * as Api from '../../utils/api/api';
import {CheckDraftComment, CheckUnevaluatedLines} from "components/popup/ApprovePopupValidations";
import {approveMultipleTasks} from "components/mytasks/action/approveMultipleTasks.action.js";
import {checkForUnevaluatedLines} from "components/mytasks/action/taskDetailsCheck";
import DisplayProgress from "components/common/displayProgress";
import { Button, ButtonType } from "components/common/Button.component";

//this is to make sure the tabbing stays inside the dialog
const TAB_INDEX = 1000;

/**
 * popup which will be used in case user has selected 'approve with comment'
 * expects as props:
 * list of selected tasks
 * close popup callback
 * translate function
 */
export class Approve extends PopUp {

    static propTypes = {
        tasks: PropTypes.array, // selected tasks
        taskDetails: PropTypes.object || null || undefined, // task details
        closeCallback: PropTypes.func, //callback for closing the popup
        handledTaskCallback: PropTypes.func, //callback to mark task as handled
        translate: PropTypes.func, // to translate texts
        showNotification: PropTypes.func, // to show transient notification
        approveMultipleTasks: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.ok = this.ok.bind(this);
        this.buildContent = this.buildContent.bind(this);
        this.focusFirst = this.focusFirst.bind(this);

        this.state = {
            comment: '',
            hideDialog: false,
            actionRunning: false, // we're waiting for backend call result
            hasUnevaluatedLines: false,
            hasTaskWithDraftComment: false,
            mayHaveUnevaluatedLines: false,
            showProgress: false,
        }
        this.commentInput = React.createRef();
        this.okButton = React.createRef();

        this.ok = this.ok.bind(this);
        this.checkForUnevaluatedLines = this.checkForUnevaluatedLines.bind(this);
        this.checkForDraftComment = this.checkForDraftComment.bind(this);
    }


    ok() {
        this.setState({
            actionRunning: true
        });


        if (this.state.hideDialog) {
            let newData = this.props.userSettings;
            newData.showCommentOnApprove = false;
            Api.updateUserSettings(newData, this.props.context);
        }

        // this redirects to myTasks
        const actionParams = this.props.actionParameters;
        this.props.approveMultipleTasks(actionParams?.tasksData, this.state.comment, undefined, undefined, false, this.props.navigate);
        this.props.closeCallback();
    }

    componentDidMount() {
        super.componentDidMount();
        this.checkForUnevaluatedLines();
        this.checkForDraftComment();

        if (this.props.showComments) {
            this.focusFirst();
        } else {
            this.okButton.current?.focus();
        }
    }

    focusFirst() {
        this.commentInput.current?.focus();
    }

    checkForUnevaluatedLines() {
        if (this.props.taskDetails?.taskPermission?.hasLinesNotEvaluated) {
            this.setState({hasUnevaluatedLines: true});
        } else {

            this.setState({showProgress: true});
            const tasksWithAccountingGrid = Object.values(this.props.actionParameters?.tasksData || {}).filter(task => task.hasAccountingGrid);

            checkForUnevaluatedLines(tasksWithAccountingGrid).then((result) => {
                this.setState({
                    hasUnevaluatedLines: result,
                    showProgress: false
                });
            }).catch((error) => {
                console.log("Error checking for unevaluated lines: ", error);
                this.setState({
                    hasUnevaluatedLines: false,
                    showProgress: false
                });
            });
        }
    }

    checkForDraftComment() {
        const findDraftComment = (tasks) => {
            return tasks.some(task => task.hasDraftComment === true);
        }

        if (findDraftComment(this.props.tasks) || this.props.hasDraftComment) {
            this.setState({hasTaskWithDraftComment: true});
        }
    }

    buildContent() {

        const okEnabled = !this.state.actionRunning;
        return (
            <div>
                <div className="modal-body">
                    {this.props.showComments ?
                        (<div>
                            <Comment
                                ref={this.commentInput}
                                inputHint={this.props.translate("popUp.approve.commentHint")}
                                translate={this.props.translate}
                                autoFocus={true}
                                tabIndex={TAB_INDEX}
                                id="comment"
                                value={this.state.comment}
                                propagateValue={value => this.setState({comment: value})}
                                label={this.props.translate("popUp.approve.commentLabel")}
                            />
                            <div
                                className={`checkbox my-3 ${(this.state.hasUnevaluatedLines || this.state.hasTaskWithDraftComment) ? "disabled" : ""} `}>
                                <input type="checkbox"
                                       id="hideApproveDialog"
                                       onChange={event => this.setState({hideDialog: event.target.checked})}/>
                                <label className="form-check-label" htmlFor="hideApproveDialog">
                                    {this.props.translate("popUp.approve.hideDialog")}
                                </label>
                            </div>
                        </div>) :
                        (
                            <div
                                className={"mb-4"}>{this.props.translate("popUp.approve.approveNTasks", this.props.tasks.length)}</div>
                        )
                    }

                    {this.state.hasTaskWithDraftComment &&
                        <CheckDraftComment
                            numberOfTasks={this.props.tasks.length}
                            showComments={this.props.showComments}
                            translate={this.props.translate}
                        />
                    }

                    {this.state.showProgress &&
                        <DisplayProgress message={this.props.translate("popUp.approve.checkingForUnevaluatedLines")}/>
                    }

                    {(this.state.hasUnevaluatedLines) &&
                        <CheckUnevaluatedLines
                            hasUnevaluatedLines={this.state.hasUnevaluatedLines}
                            translate={this.props.translate}/>
                    }
                </div>

                <div className="modal-footer">
                    <div className="d-flex gap-8 float-right">
                        <Button type={ButtonType.PRIMARY}
                                onClick={this.ok}
                                disabled={!okEnabled}
                                ref={this.okButton}
                                tabIndex={TAB_INDEX + 1}
                                text={this.props.translate("popUp.ok")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onBlur={this.focusFirst}
                                onClick={this.props.closeCallback}
                                tabIndex={TAB_INDEX + 2}
                                text={this.props.translate("popUp.cancel")}
                        />
                    </div>
                </div>
            </div>

        )
            ;
    }

    getType() {
        return TASK_ACTIONS.APPROVE;
    }

    getTitle() {
        if (this.props.tasks.length > 1) {
            return this.props.translate("popUp.approve.multipleApproveTitle");
        }
        return this.props.translate("popUp.approve.title");
    }

}

const withTranslations = translate(Approve);
//export default withTranslations;

const mapStateToProps = function (store) {
    return {
        userSettings: getUsersSettings(store),
        hasDraftComment: getTaskDraftCommentText(store),
        actionParameters: getActionParameters(store),
        context: getUsersCurrentContextId(store)
    };
};

const mapDispatchToProps = {
    approveMultipleTasks,
};

const connected = connect(mapStateToProps, mapDispatchToProps)(withTranslations);
export default connected;